(() => {
  if (customElements.get('tsl-battery-packages')) return;

  class TSLBatteryPackages extends HTMLElement {
    connectedCallback() {
      if (this.dataset.initialized === 'true') return;
      this.dataset.initialized = 'true';

      this.radiusKm = Number(this.dataset.radiusKm || 50);
      this.maxCableMeters = Number(this.dataset.maxCableMeters || 5);
      this.cartAddUrl = this.dataset.cartAddUrl || '/cart/add.js';
      this.cartUrl = this.dataset.cartUrl || '/cart';
      this.surveyUrl = this.dataset.surveyUrl || '';
      this.alternatives = this.querySelector('[data-package-alternatives]');
      this.cards = Array.from(this.querySelectorAll('[data-pricing-row]'))
        .map((row) => this.prepareCard(row))
        .filter(Boolean);

      this.handleRecommendation = (event) => this.recommend(event.detail || {});
      this.handleShowAll = (event) => this.showAll(Boolean(event.detail?.scroll));
      this.handleClear = () => this.clearRecommendation();
      this.handleReset = () => this.resetView();
      document.addEventListener('tsl:package-recommendation', this.handleRecommendation);
      document.addEventListener('tsl:show-all-packages', this.handleShowAll);
      document.addEventListener('tsl:clear-package-recommendation', this.handleClear);
      document.addEventListener('tsl:reset-package-view', this.handleReset);

      const params = new URLSearchParams(window.location.search);
      const recommendedKey = params.get('tsl_package');
      if (recommendedKey) {
        if (recommendedKey.startsWith('sigen-')) {
          this.showAll(false);
        } else {
          this.recommend({ key: recommendedKey });
        }
        if (window.location.hash === `#${this.id}` || window.location.hash === '#batterijpakketten') {
          this.showAll(true);
        }
      }
    }

    disconnectedCallback() {
      document.removeEventListener('tsl:package-recommendation', this.handleRecommendation);
      document.removeEventListener('tsl:show-all-packages', this.handleShowAll);
      document.removeEventListener('tsl:clear-package-recommendation', this.handleClear);
      document.removeEventListener('tsl:reset-package-view', this.handleReset);
    }

    prepareCard(row) {
      const jsonNode = row.querySelector('[data-package-json]');
      if (!jsonNode) return null;

      let data;
      try {
        data = JSON.parse(jsonNode.textContent);
      } catch (error) {
        console.error('TSL pakketdata kon niet worden gelezen.', error);
        return null;
      }

      const card = {
        row,
        data,
        panel: row.querySelector('[data-installation-panel]'),
        selfButton: row.querySelector('[data-package-action="self"]'),
        installedButton: row.querySelector('[data-package-action="installed"]'),
        rowStatus: row.querySelector('[data-row-status]'),
        postcode: row.querySelector('[data-postcode]'),
        conditionsConfirm: row.querySelector('[data-conditions-confirm]'),
        confirmButton: row.querySelector('[data-confirm-installation]'),
        installStatus: row.querySelector('[data-installation-status]'),
      };

      card.selfButton?.addEventListener('click', () => this.handleSelfAction(card));
      card.installedButton?.addEventListener('click', () => this.openInstallation(card));
      card.confirmButton?.addEventListener('click', () => this.handleInstalledAction(card));
      [card.postcode, card.conditionsConfirm].filter(Boolean).forEach((field) => {
        field.addEventListener('input', () => this.clearStatus(card));
        field.addEventListener('change', () => this.clearStatus(card));
      });

      this.updateActionState(card);
      return card;
    }

    updateActionState(card) {
      const selfLabel = card.selfButton?.querySelector('[data-self-label]');
      const selfAvailable = Boolean(card.data.hardware?.available);
      if (card.selfButton) card.selfButton.disabled = !selfAvailable;
      if (selfLabel) {
        selfLabel.textContent = selfAvailable
          ? (card.data.installationAvailable ? 'Alleen hardware' : 'Bestellen')
          : 'Tijdelijk niet beschikbaar';
      }

      if (!card.data.installationAvailable) return;
      const installedReady = this.productsReady(card, 'installed');
      const installedAvailable = installedReady && this.selectedProducts(card, 'installed').every((product) => product.available);
      const installedLabel = card.installedButton?.querySelector('[data-installed-label]');
      const confirmLabel = card.confirmButton?.querySelector('[data-confirm-label]');

      if (card.installedButton) card.installedButton.disabled = !installedAvailable;
      if (card.confirmButton) card.confirmButton.disabled = !installedAvailable;
      if (installedLabel) installedLabel.textContent = installedAvailable ? 'Kies pakket' : 'Tijdelijk niet beschikbaar';
      if (confirmLabel) confirmLabel.textContent = installedAvailable ? 'Bestellen' : 'Tijdelijk niet beschikbaar';
    }

    handleSelfAction(card) {
      this.clearStatus(card);
      if (!this.productsReady(card, 'self') || !card.data.hardware.available) {
        this.setStatus(card, 'Dit pakket is tijdelijk niet beschikbaar.', false, false);
        return;
      }
      this.addPackageToCart(card, 'self', card.selfButton);
    }

    openInstallation(card) {
      if (!card.panel) return;
      this.cards.forEach((other) => {
        if (other !== card && other.panel) other.panel.hidden = true;
      });
      card.panel.hidden = false;
      window.requestAnimationFrame(() => {
        card.panel.scrollIntoView({
          behavior: this.reducedMotion() ? 'auto' : 'smooth',
          block: 'nearest',
        });
        card.postcode?.focus({ preventScroll: true });
      });
    }

    handleInstalledAction(card) {
      this.clearStatus(card);
      if (!this.productsReady(card, 'installed')) {
        this.setStatus(card, 'De installatie-optie is tijdelijk niet beschikbaar.', true, false);
        return;
      }
      if (!this.validateInstallation(card)) return;
      this.addPackageToCart(card, 'installed', card.confirmButton);
    }

    async addPackageToCart(card, mode, button) {
      if (!button || button.disabled || button.classList.contains('is-loading')) return;

      const installed = mode === 'installed';
      const postcode = card.postcode?.value.trim().toUpperCase() || '';
      const commonProperties = {
        '_TSL pakket': card.data.title,
        '_Fase': card.data.phaseLabel,
      };
      const items = [{
        id: card.data.hardware.id,
        quantity: 1,
        properties: {
          ...commonProperties,
          Pakket: card.data.title,
          Service: installed ? 'Volledig geïnstalleerd' : 'Zelf installatie regelen',
        },
      }];

      if (installed) {
        const surveyProperty = this.surveyUrl ? { '_Schouwformulier': this.surveyUrl } : {};
        items.push(
          {
            id: card.data.installation.id,
            quantity: 1,
            parent_id: card.data.hardware.id,
            properties: {
              ...commonProperties,
              '_Pakketonderdeel': 'Installatieservice',
              Installatielocatie: postcode,
              Installatievoorwaarden: 'Gelezen en bevestigd',
              ...surveyProperty,
            },
          },
          {
            id: card.data.kit.id,
            quantity: 1,
            parent_id: card.data.hardware.id,
            properties: { ...commonProperties, '_Pakketonderdeel': 'Installatiekit' },
          },
        );
      }

      const label = button.querySelector('span');
      const originalLabel = label?.textContent || '';
      button.classList.add('is-loading');
      button.disabled = true;
      button.setAttribute('aria-busy', 'true');
      if (label) label.textContent = 'Wordt toegevoegd…';

      try {
        const response = await fetch(this.cartAddUrl, {
          method: 'POST',
          credentials: 'same-origin',
          headers: { 'Content-Type': 'application/json', Accept: 'application/json' },
          body: JSON.stringify({ items }),
        });
        if (!response.ok) {
          const payload = await response.json().catch(() => ({}));
          throw new Error(payload.description || payload.message || 'Het pakket kon niet worden toegevoegd.');
        }

        this.setStatus(card, 'Pakket toegevoegd. De winkelwagen wordt geopend…', installed, true);
        window.setTimeout(() => window.location.assign(this.cartUrl), 300);
      } catch (error) {
        button.classList.remove('is-loading');
        button.disabled = false;
        button.removeAttribute('aria-busy');
        if (label) label.textContent = originalLabel;
        this.setStatus(card, error.message || 'Er ging iets mis. Probeer het opnieuw.', installed, false);
      }
    }

    validateInstallation(card) {
      const postcode = card.postcode?.value.trim() || '';
      if (postcode.length < 4) {
        this.setStatus(card, 'Vul de postcode van de installatielocatie in.', true, false);
        card.postcode?.focus();
        return false;
      }
      if (!card.conditionsConfirm?.checked) {
        this.setStatus(card, 'Bevestig dat u de standaard installatievoorwaarden heeft gelezen.', true, false);
        card.conditionsConfirm?.focus();
        return false;
      }
      return true;
    }

    productsReady(card, mode) {
      if (!card.data.hardware) return false;
      if (mode === 'installed') return Boolean(card.data.installation && card.data.kit);
      return true;
    }

    selectedProducts(card, mode) {
      const products = [card.data.hardware].filter(Boolean);
      if (mode === 'installed') products.push(card.data.installation, card.data.kit);
      return products.filter(Boolean);
    }

    recommend(detail) {
      const recommendedCard = this.cards.find((item) => item.data.key === detail.key);
      this.cards.forEach((card) => {
        const active = card === recommendedCard;
        card.row.classList.toggle('is-recommended', active);
        if (active) card.row.setAttribute('aria-current', 'true');
        else card.row.removeAttribute('aria-current');
      });
    }

    clearRecommendation() {
      this.cards.forEach((card) => {
        card.row.classList.remove('is-recommended');
        card.row.removeAttribute('aria-current');
      });
    }

    resetView() {
      this.clearRecommendation();
      if (this.alternatives) this.alternatives.open = false;
      this.cards.forEach((card) => {
        if (card.panel) card.panel.hidden = true;
      });
    }

    showAll(scroll) {
      if (this.alternatives) this.alternatives.open = true;
      if (scroll) {
        window.requestAnimationFrame(() => (this.alternatives || this).scrollIntoView({
          behavior: this.reducedMotion() ? 'auto' : 'smooth',
          block: 'start',
        }));
      }
    }

    setStatus(card, message, installed, success) {
      const status = installed ? card.installStatus : card.rowStatus;
      if (!status) return;
      status.textContent = message;
      status.hidden = !message;
      status.classList.toggle('is-success', Boolean(success));
    }

    clearStatus(card) {
      [card.rowStatus, card.installStatus].filter(Boolean).forEach((status) => {
        status.textContent = '';
        status.hidden = true;
        status.classList.remove('is-success');
      });
    }

    reducedMotion() {
      return window.matchMedia('(prefers-reduced-motion: reduce)').matches;
    }
  }

  customElements.define('tsl-battery-packages', TSLBatteryPackages);
})();
